<template>
  <v-card-text class="modal-body">
    <div class="modal-header">
      <div class="modal-copy">
        <p class="modal-title">Approve reschedule request?</p>
        <p class="modal-subtitle">
          Confirm the new date and time for {{ appointmentNo }}.
        </p>
      </div>
    </div>

    <div class="field-stack">
      <div class="field-group">
        <AppDateField
          v-model="preferredDate"
          label="Date"
          label-icon="mdi-calendar-outline"
          variant="outlined"
          hide-details="auto"
          prepend-icon=""
          placeholder="Select a date"
        />
        <p v-if="dateError" class="field-error">{{ dateError }}</p>
      </div>

      <div class="field-group">
        <AppTimeField
          v-model="preferredTime"
          label="Time"
          label-icon="mdi-clock-outline"
          variant="outlined"
          hide-details="auto"
        />
        <p v-if="timeError" class="field-error">{{ timeError }}</p>
      </div>
    </div>
  </v-card-text>

  <v-card-actions>
    <div class="modal-actions">
      <AppButtonFlat
        size="small"
        color="secondary"
        variant="outlined"
        @click="modal.close()"
        :disabled="isSubmitting"
      >
        Cancel
      </AppButtonFlat>
      <AppButtonFlat
        size="small"
        color="success"
        @click="handleConfirm"
        :disabled="isSubmitting || !canConfirm"
      >
        <span class="action-button-content">
          <v-progress-circular
            v-if="isSubmitting"
            indeterminate
            :size="14"
            :width="2"
            class="action-button-spinner"
          />
          <v-icon v-else size="16">mdi-check-circle-outline</v-icon>
          <span>Approve</span>
        </span>
      </AppButtonFlat>
    </div>
  </v-card-actions>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useModalStore } from "@/shared/stores/modal";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import AppDateField from "@/shared/components/ui/AppDateField.vue";
import AppTimeField from "@/shared/components/ui/AppTimeField.vue";
import { approveAppointmentReschedule } from "@/shared/services/appointments.api";

const props = defineProps<{
  appointment: Record<string, any>;
  onSuccess?: () => void;
}>();

const modal = useModalStore();
const snackbar = useSnackbarStore();
const isSubmitting = ref(false);
const preferredDate = ref<Date | string | null>(null);
const preferredTime = ref("");
const dateError = ref("");
const timeError = ref("");

const appointmentNo = computed(() =>
  String(props.appointment?.appointment_no || "this appointment"),
);
const canConfirm = computed(() => Boolean(preferredDate.value) && preferredTime.value.trim().length > 0);

watch(preferredDate, () => {
  if (dateError.value) dateError.value = "";
});

watch(preferredTime, () => {
  if (timeError.value) timeError.value = "";
});

watch(
  () => props.appointment,
  () => {
    preferredDate.value = toDateValue(props.appointment?.reschedule_request?.preferred_date);
    preferredTime.value = normalizeTimeValue(props.appointment?.reschedule_request?.preferred_time);
  },
  { immediate: true },
);

async function handleConfirm() {
  if (isSubmitting.value) return;

  const normalizedDate = toIsoDate(preferredDate.value);
  const normalizedTime = toIsoTime(preferredTime.value);

  if (!normalizedDate) {
    dateError.value = "Please select a date.";
  }
  if (!normalizedTime) {
    timeError.value = "Please select a time.";
  }
  if (!normalizedDate || !normalizedTime) {
    return;
  }

  isSubmitting.value = true;

  try {
    const appointmentId = props.appointment?.id;
    if (!appointmentId) {
      throw new Error("Appointment id is missing.");
    }

    await approveAppointmentReschedule(appointmentId, {
      preferred_date: normalizedDate,
      preferred_time: normalizedTime,
    });

    snackbar.success("Reschedule request approved.");
    if (props.onSuccess) props.onSuccess();
    modal.close();
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Unable to approve reschedule request.";
    snackbar.error(message);
  } finally {
    isSubmitting.value = false;
  }
}

function toDateValue(value: string | null | undefined): Date | string | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];

  return raw;
}

function normalizeTimeValue(value: string | null | undefined): string {
  if (!value) return "";
  return String(value).trim();
}

function toIsoDate(value: Date | string | null): string {
  if (!value) return "";
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return "";

    const isoDateMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDateMatch) return isoDateMatch[1];
    return raw;
  }
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toIsoTime(value: string): string {
  const raw = value.trim();
  if (!raw) return "";

  const twentyFourHourMatch = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (twentyFourHourMatch) {
    const hours = twentyFourHourMatch[1].padStart(2, "0");
    const minutes = twentyFourHourMatch[2];
    const seconds = (twentyFourHourMatch[3] || "00").padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  const twelveHourMatch = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])$/);
  if (twelveHourMatch) {
    let hours = Number(twelveHourMatch[1]);
    const minutes = twelveHourMatch[2];
    const seconds = (twelveHourMatch[3] || "00").padStart(2, "0");
    const period = twelveHourMatch[4].toLowerCase();

    if (period === "pm" && hours < 12) hours += 12;
    if (period === "am" && hours === 12) hours = 0;

    return `${String(hours).padStart(2, "0")}:${minutes}:${seconds}`;
  }

  return raw;
}
</script>

<style scoped>
.modal-body {
  padding: 16px 16px 8px;
}

.modal-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.field-stack {
  margin-top: 16px;
  display: grid;
  gap: 16px;
}

.modal-actions {
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 16px 12px;
}

.action-button-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.action-button-spinner {
  color: currentColor;
}

.field-error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #dc2626;
}
</style>
